// Confirmation before a report's `goblic.submit(data)` reaches Goblic
// (design §5). Nothing is sent without a click here. Focus starts on Cancel
// and Send only becomes clickable after a short delay, so a click or Enter
// the report provoked just before opening the dialog can't confirm it.

import { useEffect, useState } from 'react'
import { Dialog } from '../ui'

/** How long Send stays disabled after the dialog opens. */
const SEND_ARM_DELAY_MS = 500
import { ReportDataView } from './report-data-view'

export interface ReportSubmitDialogProps {
  open: boolean
  /** Normalized JSON the report wants to send. */
  data: unknown
  /** Report title, to say who is asking. */
  reportTitle?: string
  sending?: boolean
  /** Error from a previous attempt (e.g. secret detected, rate limited). */
  error?: string | null
  onSend: () => void
  onCancel: () => void
}

export function ReportSubmitDialog({
  open,
  data,
  reportTitle,
  sending = false,
  error,
  onSend,
  onCancel,
}: ReportSubmitDialogProps) {
  const [armed, setArmed] = useState(false)
  useEffect(() => {
    if (!open) {
      setArmed(false)
      return
    }
    const timer = window.setTimeout(() => setArmed(true), SEND_ARM_DELAY_MS)
    return () => window.clearTimeout(timer)
  }, [open])

  return (
    <Dialog
      open={open}
      // Escape / backdrop cancel, but not while the request is in flight.
      onClose={sending ? () => undefined : onCancel}
      size="md"
      title="Send data to Goblic?"
      description={
        <>
          This report{reportTitle ? <> (“{reportTitle}”)</> : null} wants to
          send the following to Goblic. Agents working on this task can read it.
        </>
      }
    >
      <div className="max-h-[50vh] overflow-y-auto rounded-control border border-line bg-sunken/60 px-4 py-3">
        <ReportDataView data={data} />
      </div>
      <p className="mt-3 text-sm text-ink-3">
        The report was made by an agent. Don’t send passwords or tokens —
        Goblic refuses them.
      </p>
      {error ? (
        <p role="alert" className="mt-3 text-sm font-medium text-danger">
          {error}
        </p>
      ) : null}
      <div className="mt-5 flex justify-end gap-2">
        <button
          type="button"
          className="btn-ghost"
          onClick={onCancel}
          disabled={sending}
          data-autofocus
        >
          Cancel
        </button>
        <button
          type="button"
          className="btn-primary"
          onClick={onSend}
          disabled={sending || !armed}
        >
          {sending ? 'Sending…' : 'Send'}
        </button>
      </div>
    </Dialog>
  )
}
